import { InformationCircleIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';
import { useIntercom } from 'react-use-intercom';

import { useAuth } from '@restworld/feature-authentication';
import { Button, Heading, IconButton, Tooltip } from '@restworld/ui-ds-base';
import { cn, useIsFreemiumUser } from '@restworld/utils-common';

import { EmployerPageContentWrapper } from '../../components/employer-page-content-wrapper';
import DashboardFeedbackCard from './dashboard-feedback-card';
import DashboardReferralCard from './dashboard-referral-card';
import ActivityList from './activity-list';
import RestaurantOpenJobPositionsSection from '../restaurants/details/sections/restaurant-open-job-positions-section';
import useEmployerRestaurantsQuery from '../restaurants/hooks/use-employer-restaurants';
import useEmployerRestaurantCards from '../restaurants/hooks/use-employer-restaurant-cards';
import useMissedOrganisationApplicationActivitiesQuery, {
  useMissedOrganisationApplicationActivities,
} from './hooks/use-missed-organisation-application-activities-query';
import FreemiumUserUpgradeCard from './freemium-user-upgrade-card';
import ShowcaseNextSteps from '../restaurants/details/sections/restuarant-showcase-section/showcase-next-steps';
import { useOpenNewJobPosition } from '../job-listing/hooks/use-open-new-job-position';
import UnpaidInvoicesModal from '../job-position-onboarding/modals/unpaid-invoices-modal';
import { useMemo } from 'react';

function DashboardPageContent() {
  const { t } = useTranslation('dashboard-page');
  const { startTour } = useIntercom();
  const { user } = useAuth();
  const name = user?.name;
  useMissedOrganisationApplicationActivitiesQuery();
  const isFreemiumUser = useIsFreemiumUser();
  const { restaurants } = useEmployerRestaurantsQuery({ enabled: isFreemiumUser });
  const restaurantCards = useEmployerRestaurantCards({ restaurants });
  const hasMissedActivities = useMissedOrganisationApplicationActivities()?.hits > 0;
  const shouldShowActivityList = !isFreemiumUser || (isFreemiumUser && hasMissedActivities);
  const {
    openNewJobPosition,
    isAvailable: canOpenNewSearch,
    isDisabled: isNewSearchDisabled,
    disabledTooltip: newSearchDisabledTooltip,
    isBusy: isOpeningNewSearch,
  } = useOpenNewJobPosition({ source: 'homepage' });

  const singleRestaurant = useMemo(() => {
    if (restaurants.length === 1) return restaurants[0].restaurant;
    else return null;
  }, [restaurants]);

  const newSearchButton = canOpenNewSearch ? (
    // Same treatment as the job positions page header: a real `disabled` attribute would
    // set pointer-events to none and the tooltip explaining the ATS rule would never open,
    // so the button is only styled and announced as disabled.
    <Button
      variant="primary"
      size="md"
      StartIcon={PlusIcon}
      className={cn('w-full md:w-fit', isNewSearchDisabled && 'opacity-50 !cursor-not-allowed')}
      loading={isOpeningNewSearch}
      onClick={isNewSearchDisabled ? undefined : openNewJobPosition}
      aria-disabled={isNewSearchDisabled || undefined}
      id="homepage-open-new-search-button"
    >
      {t('open_a_new_search')}
    </Button>
  ) : null;

  return (
    <PageWrapper>
      {/* The greeting row sits above the two columns so the CTA aligns with the right edge
          of the page rather than with the end of the left column. */}
      <GreetingRowWrapper>
        <Heading size={3} weight="semibold" color="neutral">
          {t('hey_key')}, {name}! 👋
        </Heading>
        {newSearchButton && newSearchDisabledTooltip ? (
          <Tooltip content={newSearchDisabledTooltip} placement="bottom">
            {newSearchButton}
          </Tooltip>
        ) : (
          newSearchButton
        )}
      </GreetingRowWrapper>
      <MainContentWrapper>
        <ColOneWrapper>
          {hasMissedActivities && (
            <SubHeadingRowWrapper>
              <Heading size={5} weight="medium" color="neutral">
                {t('heres_what_happened_while_you_were_away')}...
              </Heading>
              <IconButton
                ariaLabel={t('view_info')}
                Icon={InformationCircleIcon}
                onClick={() => startTour(597872)}
                className="hidden lg:block" // Intercom product tours don't work from mobile
              />
            </SubHeadingRowWrapper>
          )}
          {shouldShowActivityList && (
            <ColOneContentWrapper className="mb-16" id="homepage-activity-feed-container">
              <ActivityList />
            </ColOneContentWrapper>
          )}
          {!!restaurantCards.length && (
            <>
              <div>
                <Heading size={5} weight="medium" color="neutral" className="mb-3">
                  {t('your_venues')}
                </Heading>
                <div className="flex flex-col gap-4">{isFreemiumUser ? restaurantCards : null}</div>
              </div>
              {!!singleRestaurant && (
                <ShowcaseNextSteps
                  slug={singleRestaurant?.slug}
                  inputRestaurantDetails={singleRestaurant}
                  variant="pyramid"
                  containerTw={'!px-0'}
                />
              )}
            </>
          )}
          <RestaurantOpenJobPositionsSection isDashboardView />
        </ColOneWrapper>

        <ColTwoWrapper>
          <ColTwoContentWrapper>
            <CardsWrapper>
              {isFreemiumUser && (
                <CardWrapper>
                  <FreemiumUserUpgradeCard />
                </CardWrapper>
              )}
              <CardWrapper>
                <DashboardReferralCard />
              </CardWrapper>
              <CardWrapper>
                <DashboardFeedbackCard />
              </CardWrapper>
            </CardsWrapper>
          </ColTwoContentWrapper>
        </ColTwoWrapper>
      </MainContentWrapper>
      <UnpaidInvoicesModal />
    </PageWrapper>
  );
}

export default DashboardPageContent;

const PageWrapper = ({ className, ...props }: any) => (
  <EmployerPageContentWrapper
    className={cn('xl:px-10 xl:overflow-visible lg:px-20 2xl:px-0 bg-neutral-10', className)}
    {...props}
  />
);
const MainContentWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn('w-full flex flex-col gap-16 xl:flex-row xl:justify-between xl:gap-10 2xl:gap-20', className)}
    {...props}
  />
);
const ColOneWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('w-full xl:min-w-[720px] xl:max-w-[750px] 2xl:min-w-[880px] 2xl:', className)} {...props} />
);
const ColTwoWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('w-full flex xl:justify-end xl:pt-[92px] xl:sticky xl:top-0 xl:h-fit', className)} {...props} />
);
// Full page width, so justify-between pushes the CTA to the right edge of the page. On
// mobile it stacks under the greeting and takes the full width, like the cards below it.
const GreetingRowWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'w-full pb-[48px] flex flex-col items-start gap-4 md:flex-row md:items-center md:justify-between md:gap-6',
      className
    )}
    {...props}
  />
);
const SubHeadingRowWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('pb-3 flex items-center justify-between', className)} {...props} />
);
const ColOneContentWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('', className)} {...props} />
);
const ColTwoContentWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('w-full xl:min-w-[20rem] xl:max-w-[20rem] xl:z-10 xl:h-fit', className)} {...props} />
);
const CardsWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-6 md:flex-row xl:flex-col', className)} {...props} />
);
const CardWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex-1 flex flex-col', className)} {...props} />
);
